import React, { useState, useEffect } from 'react';
import axios from 'axios';

const MONTH_LABELS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const CLIENT_COLORS = ['#3b82f6','#8b5cf6','#10b981','#f59e0b','#ef4444'];

export default function Reports() {
    const [period, setPeriod] = useState('2025-26');
    const [activeTab, setActiveTab] = useState('overview');
    const [stats, setStats] = useState(null);
    const [reports, setReports] = useState(null);
    const [loading, setLoading] = useState(true);

    const fetchData = () => {
        setLoading(true);
        Promise.all([
            axios.get('/api/stats'),
            axios.get('/api/reports')
        ]).then(([s, r]) => {
            setStats(s.data);
            setReports(r.data);
        }).catch(() => {
            setStats(null);
            setReports(null);
        }).finally(() => setLoading(false));
    };

    useEffect(() => { fetchData(); }, []);

    const revenueData = reports?.revenueByMonth || new Array(12).fill(0);
    const topClients  = reports?.topClients || [];
    const maxRevenue  = Math.max(...revenueData, 1);
    const totalRevenue = stats?.totalRevenue ?? 0;
    const totalPending = stats?.totalPending ?? 0;
    const totalOverdue = stats?.totalOverdue ?? 0;
    const totalInvoices   = stats?.totalInvoices ?? 0;
    const paidInvoices    = stats?.paidInvoices ?? 0;
    const overdueInvoices = stats?.overdueInvoices ?? 0;
    const totalCustomers  = stats?.totalCustomers ?? 0;
    const totalOrders     = stats?.totalOrders ?? 0;
    const netProfit = totalRevenue * 0.82; // estimated after ~18% expense
    const collectionRate = totalInvoices > 0 ? Math.round((paidInvoices / totalInvoices) * 100) : 0;

    const isEmpty = !loading && totalRevenue === 0 && totalInvoices === 0 && totalCustomers === 0;

    return (
        <>
            <style>{`
                .rep-stat { background:#fff; border-radius:14px; padding:22px 24px; border:1px solid #f1f5f9; box-shadow:0 2px 12px -4px rgba(0,0,0,0.06); transition:all 0.2s; }
                .rep-stat:hover { transform:translateY(-2px); box-shadow:0 8px 24px -6px rgba(0,0,0,0.1); }
                .rep-tab { padding:8px 20px; border-radius:9px; border:none; font-family:'Inter',sans-serif; font-weight:600; font-size:13px; cursor:pointer; transition:all 0.2s; }
                .rep-tab.active { background:#0f172a; color:#fff; }
                .rep-tab:not(.active) { background:transparent; color:#64748b; }
                .rep-tab:not(.active):hover { background:#f1f5f9; color:#0f172a; }
                .bar-group { display:flex; align-items:flex-end; gap:4px; height:100%; }
                .bar { border-radius:4px 4px 0 0; transition:all 0.4s ease; cursor:pointer; min-height:2px; }
                .bar:hover { filter:brightness(0.85); }
                .client-row { display:flex; align-items:center; gap:12px; padding:12px 0; border-bottom:1px solid #f1f5f9; }
                .client-row:last-child { border-bottom:none; }
                .empty-card { background:#f8fafc; border:2px dashed #e2e8f0; border-radius:16px; padding:60px; text-align:center; }
            `}</style>

            <div style={{ display:'flex', flexDirection:'column', gap:'20px', fontFamily:'Inter,sans-serif' }}>
                {/* Header */}
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:'12px' }}>
                    <div>
                        <h2 style={{ margin:0, fontSize:'22px', fontWeight:'800', color:'#0f172a' }}>📊 Reports & Analytics</h2>
                        <p style={{ margin:'4px 0 0', fontSize:'13px', color:'#94a3b8' }}>
                            {loading ? 'Loading live data…' : 'Live data from backend • No dummy values'}
                        </p>
                    </div>
                    <div style={{ display:'flex', gap:'10px', alignItems:'center' }}>
                        <select value={period} onChange={e => setPeriod(e.target.value)}
                            style={{ padding:'9px 14px', borderRadius:'9px', border:'1.5px solid #e2e8f0', fontSize:'13px', fontFamily:'Inter,sans-serif', fontWeight:'600', color:'#0f172a', background:'#fff', outline:'none', cursor:'pointer' }}>
                            <option value="2024-25">FY 2024-25</option>
                            <option value="2025-26">FY 2025-26</option>
                            <option value="2026-27">FY 2026-27</option>
                        </select>
                        <button onClick={fetchData}
                            style={{ padding:'9px 16px', borderRadius:'9px', border:'1.5px solid #e2e8f0', background:'#fff', color:'#475569', fontWeight:'600', fontSize:'13px', cursor:'pointer', fontFamily:'Inter,sans-serif' }}>
                            🔄 Refresh
                        </button>
                    </div>
                </div>

                {/* Empty state */}
                {isEmpty && (
                    <div className="empty-card">
                        <div style={{ fontSize:'48px', marginBottom:'12px' }}>📊</div>
                        <div style={{ fontSize:'18px', fontWeight:'700', color:'#0f172a', marginBottom:'8px' }}>No data yet</div>
                        <div style={{ fontSize:'14px', color:'#94a3b8', maxWidth:'400px', margin:'0 auto' }}>
                            Start by creating <strong>Invoices</strong>, recording <strong>Payments</strong>, and adding <strong>Customers</strong>. Reports will populate automatically with your real business data.
                        </div>
                    </div>
                )}

                {!isEmpty && (
                <>
                {/* KPI Cards */}
                <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'16px' }}>
                    {[
                        { label:'Total Revenue', value: totalRevenue > 0 ? `₹${(totalRevenue/1000).toFixed(1)}K` : '₹0', icon:'💰', color:'#16a34a', bg:'#dcfce7' },
                        { label:'Total Customers', value: totalCustomers, icon:'👥', color:'#3b82f6', bg:'#dbeafe' },
                        { label:'Sales Orders', value: totalOrders, icon:'🛍️', color:'#8b5cf6', bg:'#f5f3ff' },
                        { label:'Invoices Raised', value: totalInvoices, icon:'🧾', color:'#f59e0b', bg:'#fef9c3' },
                    ].map(s => (
                        <div key={s.label} className="rep-stat">
                            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
                                <div>
                                    <div style={{ fontSize:'12px', fontWeight:'600', color:'#64748b', marginBottom:'6px' }}>{s.label}</div>
                                    <div style={{ fontSize:'28px', fontWeight:'800', color:s.color }}>{s.value}</div>
                                </div>
                                <div style={{ width:'44px', height:'44px', borderRadius:'12px', background:s.bg, display:'flex', alignItems:'center', justifyContent:'center', fontSize:'22px' }}>{s.icon}</div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Tabs */}
                <div style={{ display:'flex', gap:'4px', background:'#f8fafc', borderRadius:'12px', padding:'4px', border:'1px solid #f1f5f9', width:'fit-content' }}>
                    {[['overview','📊 Overview'],['clients','👥 Top Clients'],['invoices','🧾 Invoice Status']].map(([key,lbl]) => (
                        <button key={key} className={`rep-tab ${activeTab === key ? 'active' : ''}`} onClick={() => setActiveTab(key)}>{lbl}</button>
                    ))}
                </div>

                {activeTab === 'overview' && (
                    <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap:'20px' }}>
                        {/* Revenue Chart - real data */}
                        <div style={{ background:'#fff', borderRadius:'16px', padding:'24px', border:'1px solid #f1f5f9', boxShadow:'0 2px 12px -4px rgba(0,0,0,0.06)' }}>
                            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'20px' }}>
                                <div>
                                    <div style={{ fontSize:'15px', fontWeight:'700', color:'#0f172a' }}>Monthly Revenue (Payments Received)</div>
                                    <div style={{ fontSize:'12px', color:'#94a3b8', marginTop:'2px' }}>Computed from actual payment records</div>
                                </div>
                                <div style={{ display:'flex', alignItems:'center', gap:'6px', fontSize:'12px', fontWeight:'600', color:'#3b82f6' }}>
                                    <span style={{ width:'12px', height:'12px', borderRadius:'3px', background:'#3b82f6', display:'inline-block' }}></span>Revenue
                                </div>
                            </div>
                            {revenueData.every(v => v === 0) ? (
                                <div style={{ height:'160px', display:'flex', alignItems:'center', justifyContent:'center', color:'#94a3b8', fontSize:'14px' }}>
                                    No payment data yet — record payments to see chart
                                </div>
                            ) : (
                            <div style={{ display:'flex', alignItems:'flex-end', gap:'6px', height:'160px', paddingTop:'10px' }}>
                                {revenueData.map((v, i) => (
                                    <div key={i} style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:'4px', height:'100%' }}>
                                        <div style={{ flex:1, display:'flex', alignItems:'flex-end', width:'100%' }}>
                                            <div className="bar" title={`${MONTH_LABELS[i]}: ₹${v.toLocaleString('en-IN')}`}
                                                style={{ width:'100%', height:`${(v/maxRevenue)*140}px`, background:'linear-gradient(to top,#2563eb,#60a5fa)' }}></div>
                                        </div>
                                        <div style={{ fontSize:'9px', color:'#94a3b8', fontWeight:'600' }}>{MONTH_LABELS[i]}</div>
                                    </div>
                                ))}
                            </div>
                            )}
                        </div>

                        {/* Summary Box - real data */}
                        <div style={{ background:'#fff', borderRadius:'16px', padding:'24px', border:'1px solid #f1f5f9', boxShadow:'0 2px 12px -4px rgba(0,0,0,0.06)' }}>
                            <div style={{ fontSize:'15px', fontWeight:'700', color:'#0f172a', marginBottom:'20px' }}>Financial Summary</div>
                            {[
                                { label:'Total Received', value: `₹${totalRevenue.toLocaleString('en-IN')}`, color:'#16a34a' },
                                { label:'Pending',        value: `₹${totalPending.toLocaleString('en-IN')}`, color:'#f59e0b' },
                                { label:'Overdue',        value: `₹${totalOverdue.toLocaleString('en-IN')}`, color:'#ef4444' },
                                { label:'Est. Net Profit', value: `₹${Math.round(netProfit).toLocaleString('en-IN')}`, color:'#3b82f6', bold:true },
                                { label:'Collection Rate', value: `${collectionRate}%`, color:'#10b981' },
                                { label:'Total Invoices',  value: totalInvoices, color:'#8b5cf6' },
                            ].map(r => (
                                <div key={r.label} style={{ display:'flex', justifyContent:'space-between', padding:'10px 0', borderBottom:'1px solid #f8fafc' }}>
                                    <span style={{ fontSize:'13px', color:'#64748b', fontWeight:'500' }}>{r.label}</span>
                                    <span style={{ fontSize:'13px', color:r.color, fontWeight: r.bold ? '800' : '700' }}>{r.value}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {activeTab === 'clients' && (
                    topClients.length === 0 ? (
                        <div className="empty-card">
                            <div style={{ fontSize:'36px', marginBottom:'10px' }}>👥</div>
                            <div style={{ fontSize:'15px', fontWeight:'700', color:'#475569' }}>No client data yet</div>
                            <div style={{ fontSize:'13px', color:'#94a3b8', marginTop:'4px' }}>Record payments against clients to see top client rankings here.</div>
                        </div>
                    ) : (
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'20px' }}>
                        <div style={{ background:'#fff', borderRadius:'16px', padding:'24px', border:'1px solid #f1f5f9', boxShadow:'0 2px 12px -4px rgba(0,0,0,0.06)' }}>
                            <div style={{ fontSize:'15px', fontWeight:'700', color:'#0f172a', marginBottom:'20px' }}>Top Clients by Revenue</div>
                            {topClients.map((c, i) => (
                                <div key={c.name} className="client-row">
                                    <div style={{ width:'30px', height:'30px', borderRadius:'8px', background: CLIENT_COLORS[i] || '#94a3b8', color:'white', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:'800', fontSize:'13px', flexShrink:0 }}>{i+1}</div>
                                    <div style={{ flex:1 }}>
                                        <div style={{ fontSize:'13px', fontWeight:'700', color:'#1e293b' }}>{c.name}</div>
                                        <div style={{ fontSize:'11px', color:'#94a3b8', marginTop:'2px' }}>{c.invoices} invoice{c.invoices !== 1 ? 's' : ''}</div>
                                    </div>
                                    <div style={{ textAlign:'right' }}>
                                        <div style={{ fontSize:'14px', fontWeight:'800', color:'#0f172a' }}>₹{c.amount.toLocaleString('en-IN')}</div>
                                        <div style={{ fontSize:'10px', color:'#94a3b8', marginTop:'2px' }}>{totalRevenue > 0 ? ((c.amount/totalRevenue)*100).toFixed(1) : 0}% of total</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div style={{ background:'#fff', borderRadius:'16px', padding:'24px', border:'1px solid #f1f5f9', boxShadow:'0 2px 12px -4px rgba(0,0,0,0.06)' }}>
                            <div style={{ fontSize:'15px', fontWeight:'700', color:'#0f172a', marginBottom:'20px' }}>Revenue Share</div>
                            {topClients.map((c, i) => (
                                <div key={c.name} style={{ marginBottom:'14px' }}>
                                    <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'4px' }}>
                                        <span style={{ fontSize:'12px', fontWeight:'600', color:'#475569' }}>{c.name.split(' ').slice(0,2).join(' ')}</span>
                                        <span style={{ fontSize:'12px', fontWeight:'700', color:'#0f172a' }}>{totalRevenue > 0 ? ((c.amount/totalRevenue)*100).toFixed(1) : 0}%</span>
                                    </div>
                                    <div style={{ height:'8px', borderRadius:'10px', background:'#f1f5f9', overflow:'hidden' }}>
                                        <div style={{ height:'100%', width:`${totalRevenue > 0 ? (c.amount/totalRevenue)*100 : 0}%`, background: CLIENT_COLORS[i] || '#94a3b8', borderRadius:'10px', transition:'width 0.6s ease' }}></div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                    )
                )}

                {activeTab === 'invoices' && (
                    totalInvoices === 0 ? (
                        <div className="empty-card">
                            <div style={{ fontSize:'36px', marginBottom:'10px' }}>🧾</div>
                            <div style={{ fontSize:'15px', fontWeight:'700', color:'#475569' }}>No invoices yet</div>
                            <div style={{ fontSize:'13px', color:'#94a3b8', marginTop:'4px' }}>Create invoices to see their status breakdown here.</div>
                        </div>
                    ) : (
                    <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'20px' }}>
                        {[
                            { label:'Paid', count: paidInvoices, color:'#16a34a', bg:'#dcfce7', icon:'✅' },
                            { label:'Overdue', count: overdueInvoices, color:'#dc2626', bg:'#fee2e2', icon:'🚨' },
                            { label:'Pending', count: totalInvoices - paidInvoices - overdueInvoices, color:'#ca8a04', bg:'#fef9c3', icon:'⏳' },
                        ].map(s => (
                            <div key={s.label} style={{ background:'#fff', borderRadius:'16px', padding:'24px', border:`2px solid ${s.bg}`, boxShadow:'0 2px 12px -4px rgba(0,0,0,0.06)' }}>
                                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'16px' }}>
                                    <div style={{ fontSize:'15px', fontWeight:'700', color:'#0f172a' }}>{s.icon} {s.label}</div>
                                    <div style={{ width:'36px', height:'36px', borderRadius:'10px', background:s.bg, display:'flex', alignItems:'center', justifyContent:'center', fontWeight:'800', color:s.color, fontSize:'16px' }}>{s.count}</div>
                                </div>
                                <div style={{ fontSize:'24px', fontWeight:'800', color:s.color }}>{s.count} invoice{s.count !== 1 ? 's' : ''}</div>
                                <div style={{ fontSize:'12px', color:'#94a3b8', marginTop:'8px' }}>
                                    {totalInvoices > 0 ? ((s.count/totalInvoices)*100).toFixed(1) : 0}% of all invoices
                                </div>
                            </div>
                        ))}
                    </div>
                    )
                )}
                </>
                )}
            </div>
        </>
    );
}
